import { differenceInMilliseconds, differenceInMinutes, isBefore, subMilliseconds } from "date-fns";
import Links from "fabrix/components/links";
import { useBreadcrumbs, useHasRoles, useIcon, useQueryParams, useSettings } from "fabrix/hooks";
import { MessageType } from "fabrix/redux/message-box.slice";
import { ModulesState } from "fabrix/redux/module-slice";
import { AppState } from "fabrix/redux/store";
import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router";
import { NavLink } from "react-router-dom";
import { diff } from "util";
import { translate } from "../../common";
import Footer from "../../components/footer";
import Header from "../../components/header";
import MessageBox from "../../components/message-box";
import { SessionWarning } from "../../components/session-warning";
import { JwtAuthService, timeoutCheckMinutes, timeoutCheckSec, UserCache } from "../../services";
import { getSidebarPinnedCookie, setSidebarPinnedCookie } from "../../services/session.cookies";

export default function Layout() {
  const location = useLocation();
  const [getParam] = useQueryParams();
  const [setBreadcrumbs] = useBreadcrumbs();
  const [_warningOpen, setWarningOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const icon = useIcon();
  const _appSettings = useSettings();
  const [, hasRoles] = useHasRoles();
  const initialSidebarPinned = getSidebarPinnedCookie();
  const [compactSidebar, setCompactSidebar] = useState<boolean>(typeof window !== "undefined" ? window.innerWidth < 630 : false);
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(typeof window !== "undefined" ? window.innerWidth >= 630 && initialSidebarPinned : initialSidebarPinned);
  const [sidebarPinned, setSidebarPinned] = useState<boolean>(initialSidebarPinned);
  const effectivePinned = sidebarPinned && !compactSidebar;
  const authenticationTimerRef = useRef<number | null>(null);
  const loginKeyRef = useRef(getParam("key"));
 

  const scheduleAuthenticationCheck = useCallback(() => {
    if (authenticationTimerRef.current !== null) {
      window.clearTimeout(authenticationTimerRef.current);
    }


    //If the user is almost expired, show the session warning dialog
    if (JwtAuthService.almostExpired(timeoutCheckSec)) {

      setWarningOpen(true);
    } else {
      //else we redo the check in 1 minute
      authenticationTimerRef.current = window.setTimeout(async () => {
        if (!JwtAuthService.isAuthenticated()) {
          navigate("/signin", { replace: true });
          return;
        }

        scheduleAuthenticationCheck();
      }, 1 * 60 * 1000);
    }
  }, [ navigate]);

  const handleWarningClose = (id: string) => {
    setWarningOpen(false)
    if (id === "no") {
      UserCache.clear();
      window.setTimeout(() => navigate("/signin", { replace: true }), 100);
    } else {
      scheduleAuthenticationCheck();
    }
  }

  useEffect(() => {
    let cancelled = false;
    let key = loginKeyRef.current;
    if (key) {

      let service = new JwtAuthService();
      service.keyLogin(key).then(() => {
        if (!cancelled) {
          scheduleAuthenticationCheck();
        }
      })


    } else {
      scheduleAuthenticationCheck();
    }

    return () => {
      cancelled = true;
      if (authenticationTimerRef.current !== null) {
        window.clearTimeout(authenticationTimerRef.current);
      }
    };
  }, [scheduleAuthenticationCheck]);

  const _moduleState = useSelector<AppState>((state) => state.modules) as ModulesState;
  const visibleModules = useMemo(() => {
    const userModule = _moduleState.userModules;
    const modules = _moduleState.modules.filter((x) => {
      if (x.hide) return false;
      if (x.type === "dynamic") {
        return userModule.includes(x.id);
      }
      if (x.type === "system" && x.roles && x.roles.length > 0) {
        return hasRoles(x.roles);
      }
      return true;
    });

    return [...modules].map((module) => ({ ...module, label: translate(module.label) })).sort((a, b) => a.label.localeCompare(b.label));
  }, [_moduleState.modules, _moduleState.userModules, hasRoles]);

  const closeSidebarOnMenuClick = () => {
    if (!effectivePinned) {
      setSidebarOpen(false);
    }
  };

  const toggleSidebar = () => {
    if (effectivePinned) {
      setSidebarPinned(false);
      setSidebarOpen(false);
      return;
    }
    setSidebarOpen((prev) => !prev);
  };

  const togglePinned = () => {
    if (compactSidebar) {
      setSidebarPinned(false);
      setSidebarOpen(false);
      return;
    }
    const nextPinned = !sidebarPinned;
    setSidebarPinned(nextPinned);
    if (nextPinned) {
      setSidebarOpen(true);
    } else {
      setSidebarOpen(false);
    }
  };

  useEffect(() => {
    setSidebarPinnedCookie(sidebarPinned);
  }, [sidebarPinned]);

  useEffect(() => {
    const handleResize = () => {
      const isCompact = window.innerWidth < 630;
      setCompactSidebar(isCompact);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (compactSidebar) {
      setSidebarOpen(false);
    } else if (sidebarPinned) {
      setSidebarOpen(true);
    }
  }, [compactSidebar, sidebarPinned]);

  useEffect(() => {
    let pathName = location.pathname;
    if (pathName.startsWith("/")) pathName = pathName.substring(1);
    let pathParts = pathName.split("/");
    let root = pathParts[0];
    let module = _moduleState.modules.find((x) => x.path === `/${root}`);
    if (module && _moduleState.loaded) {
      if (module.hide) {
        const translatedCredentialMessage = translate("EMPLOYEE_CENTRE_CREDENTIAL_REQUIRED");
        const credentialMessage = translatedCredentialMessage === "EMPLOYEE_CENTRE_CREDENTIAL_REQUIRED" ? "Employee Centre requires the verified credential: Government Employee." : translatedCredentialMessage;
        const message = module.name === "employee-centre" ? credentialMessage : translate("NO_MODULE_RIGHTS", module);
        navigate("/", { state: { message: message, messageType: MessageType.Error } });
        return;
      }

      if (module.type === "dynamic") {
        let userModule = _moduleState.userModules.find((x) => x === module?.id);
        if (!userModule) {
          let message = translate("NO_DYNAMIC_MODULE", module);
          navigate("/", { state: { message: message, messageType: MessageType.Error } });
        }
      } else if (module.type === "system" && module.roles) {
        if (!hasRoles(module.roles)) {
          let message = translate("NO_MODULE_RIGHTS", module);
          navigate("/", { state: { message: message, messageType: MessageType.Error } });
        }
      }
    }
    setBreadcrumbs([]);
  }, [location.pathname, _moduleState.loaded, _moduleState.modules, _moduleState.userModules, hasRoles, navigate]);

  return (
    <div className="fabrix-main">
      <Header onMenuClick={toggleSidebar} />
      <MessageBox />
      {sidebarOpen && !effectivePinned ? <button type="button" className="fabrix-sidebar-backdrop" aria-label="Close sidebar" onClick={() => setSidebarOpen(false)} /> : null}
      <div className="fabrix-shell">
        <aside className={`fabrix-app-sidebar ${effectivePinned ? "is-pinned" : "is-floating"} ${sidebarOpen ? "is-open" : "is-collapsed"}`}>
          <nav className="fabrix-app-sidebar-nav" aria-label="Portal Navigation">
            <div className="fabrix-app-sidebar-section">
              <div className="fabrix-app-sidebar-section-head">
                <p className="fabrix-app-sidebar-title">{translate("MAIN")}</p>
                <button type="button" className={`fabrix-app-sidebar-pin ${effectivePinned ? "is-pinned" : ""}`} aria-label={effectivePinned ? "Unpin sidebar" : "Pin sidebar"} aria-pressed={effectivePinned} title={effectivePinned ? "Unpin sidebar" : "Pin sidebar"} onClick={togglePinned}>
                  <span className="material-icons">push_pin</span>
                </button>
              </div>
              <NavLink to="/" end className={({ isActive }) => `fabrix-app-sidebar-link ${isActive ? "is-active" : ""}`} onClick={closeSidebarOnMenuClick}>
                <span className="material-icons">home</span>
                <span>{translate("HOME")}</span>
              </NavLink>
            </div>
            <div className="fabrix-app-sidebar-section">
              <p className="fabrix-app-sidebar-title">{translate("MODULES")}</p>
              {visibleModules.map((module) => {
                const moduleLinkStyle: CSSProperties & { "--fbx-sidebar-item-color"?: string } = {};
                if (module.color) {
                  moduleLinkStyle["--fbx-sidebar-item-color"] = module.color;
                }
                const iconClass = module.icon ? icon(module.icon) : undefined;

                return (
                  <NavLink key={module.id} to={module.path} className={({ isActive }) => `fabrix-app-sidebar-link fabrix-app-sidebar-link--module ${isActive ? "is-active" : ""}`} style={moduleLinkStyle} onClick={closeSidebarOnMenuClick}>
                    <span className="fabrix-app-sidebar-link-icon" aria-hidden="true">
                      {module.image ? <img src={module.image} alt="" className="fabrix-app-sidebar-link-image" /> : iconClass ? <span className={iconClass}></span> : module.icon ? <span className="material-icons">{module.icon}</span> : <span className="material-icons">apps</span>}
                    </span>
                    <span>{module.label}</span>
                  </NavLink>
                );
              })}
            </div>

            <div className="fabrix-app-sidebar-section">
              <p className="fabrix-app-sidebar-title">{translate("LINKS")}</p>
              <Links />
              {/* <div className="fabrix-app-sidebar-version-card">
              <p>Version 1.0.0</p>
              <p>© 2026 National Treasury</p>
            </div> */}
            </div>
          </nav>
        </aside>
        <div className="fabrix-outlet">
          <Outlet />

        </div>
      </div>
      <Footer />
      <SessionWarning open={_warningOpen} onClose={handleWarningClose} />
    </div>
  );
}


